<template>
  <div
    v-if="activeFilterItems && activeFilterItems.length > 0"
    class="my-0 flex w-full flex-wrap justify-center gap-2 sm:mb-4 sm:mt-2 md:mb-2 md:justify-start"
  >
    <button
      v-for="(filterItem, idx) in activeFilterItems"
      :key="idx"
      class="flex flex-nowrap items-center justify-center gap-2 whitespace-nowrap border border-lightGold-60 bg-lightGold-40 px-2 py-1 text-sm uppercase"
      type="button"
      :aria-label="
        getRemoveFilterAriaLabel(
          filterItem,
          translateLabel(filterItem.name),
          $t,
        )
      "
      @click.prevent="removeFilterItem(filterItem)"
    >
      <IconCross class="size-2.5" aria-hidden="true" focusable="false" />
      {{ translateLabel(filterItem.name) }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from 'es-toolkit/compat'
import { toCurrency } from '#layers/base/utils/price'
import { getRemoveFilterAriaLabel } from '#layers/base/utils/a11y'
import type { FilterItemWithValues } from '#layers/base/types/filter'

const props = defineProps<{
  filters: FilterItemWithValues[]
  activeFilters: Record<string, any>
}>()

const emit = defineEmits([
  'applyAttributeFilter',
  'resetPriceFilter',
  'resetFilter',
])

const i18n = useI18n()
const { filters, activeFilters } = toRefs(props)

const translateLabel = (label: string) => {
  const translationKey = `filter.${label.toLowerCase()}`
  const translatedText = i18n.t(translationKey)

  return translatedText === translationKey ? label : translatedText
}

const activeFilterItems = computed(() => {
  return getActiveFilters(filters.value, activeFilters.value)
})

const removeFilterItem = (filterItem: any) => {
  if (filterItem.slug === 'minPrice') {
    emit('resetPriceFilter')
  }

  const changedFilter = filters.value.find(
    (f: any) => f.slug === filterItem.slug,
  )

  if (!changedFilter) {
    return
  }

  if (changedFilter.type === 'boolean') {
    emit('resetFilter', filterItem.slug)
  } else {
    const filter = activeFilters.value[filterItem.slug]

    if (!Array.isArray(filter)) {
      emit('resetFilter', filterItem.slug)
      return
    }

    const changedAttributeFilter = filter.filter(
      (f: any) => f !== filterItem.id,
    )

    emit('applyAttributeFilter', filterItem.slug, changedAttributeFilter)
  }
}

function getActiveFilters(filters: any, activeFilters: any) {
  if (isEmpty(activeFilters)) {
    return []
  }
  return Object.entries(activeFilters)
    .map(([key, value]: any[]) => {
      if (key === 'minPrice') {
        return {
          id: 0,
          name: `${toCurrency(activeFilters.minPrice)} - ${toCurrency(
            activeFilters.maxPrice,
          )}`,
          slug: key as string,
          value: value as string,
          type: 'range',
        }
      }

      const filter = filters?.find((f: any) => f.slug === key)
      if (!filter) {
        console.warn(`Filter not found: "${key}"`)
        return null
      }

      if (filter.type === 'boolean') {
        return {
          id: filter.id as number,
          name: filter.name as string,
          slug: key as string,
          value: value as boolean,
          type: 'boolean',
        }
      }

      const targetIds = (Array.isArray(value) ? value : [value]).map(Number)
      const values = filter.values.filter((f: any) => targetIds.includes(f.id))

      return values.map((f: any) => ({
        id: f.id as number,
        name: (f.name.includes('|') ? f.name.split('|')[1] : f.name) as string,
        slug: key as string,
        value: f.value as string,
        category: filter.name as string,
        type: 'attributes',
      }))
    })
    .flat()
    .filter(Boolean) as Array<{
    id: number
    name: string
    slug: string
    value: string | boolean
    category?: string
    type: 'attributes' | 'boolean' | 'range'
  }>
}
</script>
